// api/usuarios.js
import { API_BASE } from '../utils/helpers';

const API_URL = `${API_BASE}/api/usuarios`;
// const API_URL = 'http://localhost:4000/api/usuarios'; // Local
// Usa el que corresponda a tu entorno
//const API_URL = API_LOCAL;

const friendlyMessage = (message) => {
  const raw = String(message || '');
  if (/duplicate key|unique|UNIQUE KEY|Violation/i.test(raw)) {
    return 'El usuario o correo ya esta registrado';
  }
  if (/telefono.*registrado|registrado.*telefono/i.test(raw)) {
    return 'Ese telefono ya esta registrado';
  }
  if (/<!DOCTYPE html>|<html/i.test(raw)) {
    return 'El servidor no tiene disponible esta ruta. Revisa que el backend este actualizado.';
  }
  if (/String or binary data|truncated/i.test(raw)) {
    return 'Uno de los campos es demasiado largo';
  }
  return raw || 'No se pudo completar la solicitud';
};

const actorHeaders = (actor) => {
  const headers = { 'Content-Type': 'application/json' };
  if (actor?.id) headers['X-User-Id'] = String(actor.id);
  if (actor?.role) headers['X-User-Role'] = String(actor.role);
  return headers;
};

const actorQuery = (actor, extra = {}) => {
  const params = new URLSearchParams(extra);
  if (actor?.id) params.set('actorId', actor.id);
  const query = params.toString();
  return query ? `?${query}` : '';
};

async function handleJson(res) {
  const text = await res.text().catch(() => '');
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {}

  if (!res.ok) {
    const msg = friendlyMessage(data?.error || data?.message || text || `Error HTTP ${res.status}`);
    return { success: false, error: msg, message: msg, status: res.status };
  }

  if (data && (data.error || data.message)) {
    return {
      ...data,
      error: data.error ? friendlyMessage(data.error) : data.error,
      message: data.message ? friendlyMessage(data.message) : data.message
    };
  }

  return data || { success: true };
}

export async function registrarUsuario(data, actor = null) {
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: actorHeaders(actor),
    body: JSON.stringify({
      NombreUsuario: data.nombreUsuario,
      // Enviamos ambos campos para compatibilidad con servidores antiguos y nuevos
      Password: data.password,
      HashPassword: data.password,
      Correo: data.correo,
      Rol: data.rol,
      NombreCompleto: data.nombreCompleto,
      Telefono: data.telefono,
      FechaNacimiento: data.fechaNacimiento ?? null, // 'YYYY-MM-DD'
      ActorUsuarioId: actor?.id || null
    })
  });
  return handleJson(res);
}

export async function obtenerUsuarios(options = {}) {
  const query = actorQuery(options.actor, {
    ...(options.includeHash ? { includeHash: '1' } : {})
  });
  const separator = query ? '&' : '?';
  const res = await fetch(`${API_URL}${query}${separator}_=${Date.now()}`, {
    headers: actorHeaders(options.actor),
    cache: 'no-store'
  });
  const data = await handleJson(res);
  return Array.isArray(data) ? data : [];
}

export async function actualizarUsuario(id, data, actor) {
  const res = await fetch(`${API_URL}/${id}`, {
    method: 'PUT',
    headers: actorHeaders(actor),
    body: JSON.stringify({
      NombreUsuario: data.nombreUsuario,
      Password: data.password || '',
      HashPassword: data.password || '',
      Correo: data.correo,
      Rol: data.rol,
      NombreCompleto: data.nombreCompleto,
      Telefono: data.telefono,
      FechaNacimiento: data.fechaNacimiento ?? null,
      Estado: data.estado || 'activo',
      ActorUsuarioId: actor?.id || null
    })
  });
  return handleJson(res);
}

export async function eliminarUsuario(id, actor) {
  const res = await fetch(`${API_URL}/${id}${actorQuery(actor)}`, {
    method: 'DELETE',
    headers: actorHeaders(actor)
  });
  return handleJson(res);
}

export async function toggleUsuarioEstado(id, actor) {
  const res = await fetch(`${API_URL}/${id}/toggle-estado${actorQuery(actor)}`, {
    method: 'PATCH',
    headers: actorHeaders(actor)
  });
  return handleJson(res);
}

export async function loginUsuario({ username, password }) {
  const res = await fetch(`${API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  });
  return handleJson(res);
}

// Nuevo flujo: restablecimiento por correo
export async function startPasswordReset(email) {
  const res = await fetch(`${API_URL}/password-reset/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  return handleJson(res);
}

export async function verifyPasswordReset({ email, code, newPassword }) {
  const res = await fetch(`${API_URL}/password-reset/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, newPassword })
  });
  return handleJson(res);
}
